import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowTrendUp, faClock, faEuroSign, faChartLine,
  faArrowRight, faIndustry, faCogs, faChartPie
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../i18n/LanguageContext';
import './CaseStudies.css';

const casesByLang = {
  fr: [
    {
      client: 'KUBITECH',
      sector: 'Industrie métallique',
      icon: faIndustry,
      title: 'Digitalisation complète de la production',
      summary: 'Déploiement de NIROPROD pour piloter la production, la facturation et la logistique en temps réel.',
      metrics: [
        { icon: faArrowTrendUp, value: '+40%', label: 'Productivité' },
        { icon: faClock, value: '-60%', label: 'Temps administratif' },
        { icon: faChartLine, value: '100%', label: 'Traçabilité' }
      ]
    },
    {
      client: 'P-S-M',
      sector: 'Mécanique de précision',
      icon: faCogs,
      title: 'Automatisation des devis & facturation',
      summary: 'Workflow automatisé pour générer devis, factures et relances client avec synchronisation comptable.',
      metrics: [
        { icon: faClock, value: '3h→15min', label: 'Par devis' },
        { icon: faEuroSign, value: '-85%', label: 'Erreurs' },
        { icon: faArrowTrendUp, value: '2×', label: 'Volume traité' }
      ]
    },
    {
      client: 'Projet pilote',
      sector: 'Production industrielle',
      icon: faChartPie,
      title: 'Analyse IA des goulots de production',
      summary: 'Modèle prédictif qui analyse les temps réels vs planifiés et détecte les postes à optimiser.',
      metrics: [
        { icon: faArrowTrendUp, value: '+25%', label: 'Efficacité poste' },
        { icon: faClock, value: '-30%', label: 'Retards livraison' },
        { icon: faChartLine, value: 'J+1', label: 'Visibilité' }
      ]
    }
  ],
  en: [
    {
      client: 'KUBITECH',
      sector: 'Metal industry',
      icon: faIndustry,
      title: 'Full production digitalization',
      summary: 'NIROPROD deployment to drive production, invoicing and logistics in real time.',
      metrics: [
        { icon: faArrowTrendUp, value: '+40%', label: 'Productivity' },
        { icon: faClock, value: '-60%', label: 'Admin time' },
        { icon: faChartLine, value: '100%', label: 'Traceability' }
      ]
    },
    {
      client: 'P-S-M',
      sector: 'Precision mechanics',
      icon: faCogs,
      title: 'Quote & invoicing automation',
      summary: 'Automated workflow generating quotes, invoices and reminders with accounting sync.',
      metrics: [
        { icon: faClock, value: '3h→15min', label: 'Per quote' },
        { icon: faEuroSign, value: '-85%', label: 'Errors' },
        { icon: faArrowTrendUp, value: '2×', label: 'Volume handled' }
      ]
    },
    {
      client: 'Pilot project',
      sector: 'Industrial production',
      icon: faChartPie,
      title: 'AI analysis of production bottlenecks',
      summary: 'Predictive model analyzing actual vs planned times and detecting workstations to optimize.',
      metrics: [
        { icon: faArrowTrendUp, value: '+25%', label: 'Station efficiency' },
        { icon: faClock, value: '-30%', label: 'Delivery delays' },
        { icon: faChartLine, value: 'D+1', label: 'Visibility' }
      ]
    }
  ],
  pl: [
    {
      client: 'KUBITECH',
      sector: 'Przemysł metalowy',
      icon: faIndustry,
      title: 'Pełna cyfryzacja produkcji',
      summary: 'Wdrożenie NIROPROD do zarządzania produkcją, fakturowaniem i logistyką w czasie rzeczywistym.',
      metrics: [
        { icon: faArrowTrendUp, value: '+40%', label: 'Produktywność' },
        { icon: faClock, value: '-60%', label: 'Czas administracyjny' },
        { icon: faChartLine, value: '100%', label: 'Śledzenie' }
      ]
    },
    {
      client: 'P-S-M',
      sector: 'Mechanika precyzyjna',
      icon: faCogs,
      title: 'Automatyzacja ofert i fakturowania',
      summary: 'Zautomatyzowany workflow generujący oferty, faktury i przypomnienia z synchronizacją księgową.',
      metrics: [
        { icon: faClock, value: '3h→15min', label: 'Na ofertę' },
        { icon: faEuroSign, value: '-85%', label: 'Błędy' },
        { icon: faArrowTrendUp, value: '2×', label: 'Obsłużona ilość' }
      ]
    },
    {
      client: 'Projekt pilotażowy',
      sector: 'Produkcja przemysłowa',
      icon: faChartPie,
      title: 'Analiza AI wąskich gardeł produkcji',
      summary: 'Model predykcyjny analizujący czasy rzeczywiste vs planowane i wykrywający stanowiska do optymalizacji.',
      metrics: [
        { icon: faArrowTrendUp, value: '+25%', label: 'Wydajność stanowiska' },
        { icon: faClock, value: '-30%', label: 'Opóźnienia dostaw' },
        { icon: faChartLine, value: 'D+1', label: 'Widoczność' }
      ]
    }
  ]
};

const CaseStudies = () => {
  const { language, t } = useLanguage();
  const cases = casesByLang[language] || casesByLang.fr;

  return (
    <section className="section section-dark case-studies">
      <div className="dot-pattern"></div>
      <div className="container">
        <div className="fade-in" style={{ textAlign: 'center', marginBottom: 56 }}>
          <span className="section-label">{t('caseStudies.label')}</span>
          <h2 className="section-title">
            {t('caseStudies.titleStart')} <span className="gradient-text">{t('caseStudies.titleHighlight')}</span>
          </h2>
          <p className="section-subtitle" style={{ margin: '0 auto' }}>
            {t('caseStudies.subtitle')}
          </p>
        </div>

        <div className="case-studies__grid">
          {cases.map((c, i) => (
            <article className={`case-study fade-in fade-in-delay-${Math.min(i, 3)}`} key={i}>
              <div className="case-study__header">
                <div className="case-study__icon">
                  <FontAwesomeIcon icon={c.icon} />
                </div>
                <div>
                  <h3 className="case-study__client">{c.client}</h3>
                  <span className="case-study__sector">{c.sector}</span>
                </div>
              </div>

              <h4 className="case-study__title">{c.title}</h4>
              <p className="case-study__summary">{c.summary}</p>

              <div className="case-study__metrics">
                {c.metrics.map((m, j) => (
                  <div className="case-study__metric" key={j}>
                    <FontAwesomeIcon icon={m.icon} className="case-study__metric-icon" />
                    <span className="case-study__metric-value">{m.value}</span>
                    <span className="case-study__metric-label">{m.label}</span>
                  </div>
                ))}
              </div>
            </article>
          ))}
        </div>

        <div className="case-studies__cta fade-in" style={{ textAlign: 'center', marginTop: 48 }}>
          <Link to="/contact" className="btn btn-outline">
            {t('caseStudies.cta')} <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
